/**
 * Shared registry of the stablecoin contracts Paytrie transacts in. Mirrors
 * apps/jersey-api/src/token-contracts.ts, which cannot import this package
 * because it runs on Deno outside the pnpm workspace.
 */
import { ChainNetwork } from './networks'

export type TokenEnvironment = 'mainnet' | 'testnet'
export type TokenOperation = 'send' | 'receive'
export type StablecoinSymbol = 'CADC' | 'USDC'
export type TokenLifecycle = 'current' | 'legacy'

export type TokenContract = {
  symbol: StablecoinSymbol
  network: ChainNetwork
  env: TokenEnvironment
  address: string
  decimals: number
  operations: readonly TokenOperation[]
  lifecycle: TokenLifecycle
}

const SEND_AND_RECEIVE: readonly TokenOperation[] = ['send', 'receive']
const RECEIVE_ONLY: readonly TokenOperation[] = ['receive']

export const LEGACY_CADC_ARB_MAINNET = {
  symbol: 'CADC',
  network: 'ARB',
  env: 'mainnet',
  address: '0x2b28E826b55e399F4d4699b85f68666AC51e6f70',
  decimals: 18,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'legacy',
} as const satisfies TokenContract

export const USDC_ARB_MAINNET = {
  symbol: 'USDC',
  network: 'ARB',
  env: 'mainnet',
  address: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const BRIDGED_USDC_ARB_MAINNET = {
  symbol: 'USDC',
  network: 'ARB',
  env: 'mainnet',
  address: '0xFF970A61A04b1cA14834A43f5dE4533eBDDB5CC8',
  decimals: 6,
  operations: RECEIVE_ONLY,
  lifecycle: 'current',
} as const satisfies TokenContract

export const USDC_AVAX_MAINNET = {
  symbol: 'USDC',
  network: 'AVAX',
  env: 'mainnet',
  address: '0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const LEGACY_CADC_BASE_MAINNET = {
  symbol: 'CADC',
  network: 'BASE',
  env: 'mainnet',
  address: '0x043eB4B75d0805c43D7C834902E335621983Cf03',
  decimals: 18,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'legacy',
} as const satisfies TokenContract

export const USDC_BASE_MAINNET = {
  symbol: 'USDC',
  network: 'BASE',
  env: 'mainnet',
  address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const USDC_BSC_MAINNET = {
  symbol: 'USDC',
  network: 'BSC',
  env: 'mainnet',
  address: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d',
  decimals: 18,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const USDC_CELO_MAINNET = {
  symbol: 'USDC',
  network: 'CELO',
  env: 'mainnet',
  address: '0xcebA9300f2b948710d2653dD7B07f33A8B32118C',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const LEGACY_CADC_ETH_MAINNET = {
  symbol: 'CADC',
  network: 'ETH',
  env: 'mainnet',
  address: '0xcaDC0acd4B445166f12d2C07EAc6E2544FbE2Eef',
  decimals: 18,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'legacy',
} as const satisfies TokenContract

export const USDC_ETH_MAINNET = {
  symbol: 'USDC',
  network: 'ETH',
  env: 'mainnet',
  address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const USDC_FTM_MAINNET = {
  symbol: 'USDC',
  network: 'FTM',
  env: 'mainnet',
  address: '0x2F733095B80A04b38b0D10cC884524a3d09b836a',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const USDC_GNO_MAINNET = {
  symbol: 'USDC',
  network: 'GNO',
  env: 'mainnet',
  address: '0xDDAfbb505ad214D7b80b1f830fcCc89B60fb7A83',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const LEGACY_CADC_LIN_MAINNET = {
  symbol: 'CADC',
  network: 'LIN',
  env: 'mainnet',
  address: '0xa0b18e70387ba72d1c7038bc0bd3a05e5a2287f6',
  decimals: 18,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'legacy',
} as const satisfies TokenContract

export const USDC_LIN_MAINNET = {
  symbol: 'USDC',
  network: 'LIN',
  env: 'mainnet',
  address: '0x176211869cA2b568f2A7D4EE941E073a821EE1ff',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const USDC_OP_MAINNET = {
  symbol: 'USDC',
  network: 'OP',
  env: 'mainnet',
  address: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const BRIDGED_USDC_OP_MAINNET = {
  symbol: 'USDC',
  network: 'OP',
  env: 'mainnet',
  address: '0x7F5c764cBc14f9669B88837ca1490cCa17c31607',
  decimals: 6,
  operations: RECEIVE_ONLY,
  lifecycle: 'current',
} as const satisfies TokenContract

export const LEGACY_CADC_POLY_MAINNET = {
  symbol: 'CADC',
  network: 'POLY',
  env: 'mainnet',
  address: '0x9de41aFF9f55219D5bf4359F167d1D0c772A396D',
  decimals: 18,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'legacy',
} as const satisfies TokenContract

export const USDC_POLY_MAINNET = {
  symbol: 'USDC',
  network: 'POLY',
  env: 'mainnet',
  address: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const BRIDGED_USDC_POLY_MAINNET = {
  symbol: 'USDC',
  network: 'POLY',
  env: 'mainnet',
  address: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174',
  decimals: 6,
  operations: RECEIVE_ONLY,
  lifecycle: 'current',
} as const satisfies TokenContract

export const USDC_SOL_MAINNET = {
  symbol: 'USDC',
  network: 'SOL',
  env: 'mainnet',
  address: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const LEGACY_CADC_SOL_MAINNET = {
  symbol: 'CADC',
  network: 'SOL',
  env: 'mainnet',
  address: '9ewjJpmD1ES83RDRFnHs7V2hUdH76WAVjdvu6UV6WNo7',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'legacy',
} as const satisfies TokenContract

export const LEGACY_CADC_SOL_TESTNET = {
  symbol: 'CADC',
  network: 'SOL',
  env: 'testnet',
  address: '9ewjJpmD1ES83RDRFnHs7V2hUdH76WAVjdvu6UV6WNo7',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'legacy',
} as const satisfies TokenContract

export const USDC_SX_MAINNET = {
  symbol: 'USDC',
  network: 'SX',
  env: 'mainnet',
  address: '0x6629Ce1Cf35Cc1329ebB4F63202F3f197b3F050B',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const USDC_ARB_TESTNET = {
  symbol: 'USDC',
  network: 'ARB',
  env: 'testnet',
  address: '0x75faf114eafb1BDbe2F0316DF893fd58CE46AA4d',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const USDC_AVAX_TESTNET = {
  symbol: 'USDC',
  network: 'AVAX',
  env: 'testnet',
  address: '0x5425890298aed601595a70AB815c96711a31Bc65',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const CADC_BASE_TESTNET = {
  symbol: 'CADC',
  network: 'BASE',
  env: 'testnet',
  address: '0x24bAb412E4dCe89252A848Fb62084a3965cb130f',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const USDC_BASE_TESTNET = {
  symbol: 'USDC',
  network: 'BASE',
  env: 'testnet',
  address: '0x036CbD53842c5426634e7929541eC2318f3dCF7e',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const USDC_CELO_TESTNET = {
  symbol: 'USDC',
  network: 'CELO',
  env: 'testnet',
  address: '0x01C5C0122039549AD1493B8220cABEdD739BC44E',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const CADC_ETH_TESTNET = {
  symbol: 'CADC',
  network: 'ETH',
  env: 'testnet',
  address: '0xFBC47E07617199B0596C5fD47b12a7faAC9d0a21',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const USDC_ETH_TESTNET = {
  symbol: 'USDC',
  network: 'ETH',
  env: 'testnet',
  address: '0x1c7D4B196Cb0C7B01d743Fbc6116a902379C7238',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const USDC_LIN_TESTNET = {
  symbol: 'USDC',
  network: 'LIN',
  env: 'testnet',
  address: '0xFEce4462D57bD51A6A552365A011b95f0E16d9B7',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const USDC_OP_TESTNET = {
  symbol: 'USDC',
  network: 'OP',
  env: 'testnet',
  address: '0x5fd84259d66Cd46123540766Be93DFE6D43130D7',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const USDC_POLY_TESTNET = {
  symbol: 'USDC',
  network: 'POLY',
  env: 'testnet',
  address: '0x41E94Eb019C0762f9Bfcf9Fb1E58725BfB0e7582',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const USDC_SOL_TESTNET = {
  symbol: 'USDC',
  network: 'SOL',
  env: 'testnet',
  address: '4zMMC9srt5Ri5X14GAgXhaHii3GnPAEERYPJgZJDncDU',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const CADC_SOL_TESTNET = {
  symbol: 'CADC',
  network: 'SOL',
  env: 'testnet',
  address: '52spLAAFjKMwhNJ3TL2PUAUGYGgXsbvipP4whRtBp82S',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const CADC_ETH_MAINNET = {
  symbol: 'CADC',
  network: 'ETH',
  env: 'mainnet',
  address: '0x49A6F6aeAB68eA001bEaa4815186eb22FBDcCF2A',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const CADC_BASE_MAINNET = {
  symbol: 'CADC',
  network: 'BASE',
  env: 'mainnet',
  address: '0x5e63E9C06c3F2c779EF29a8071F94F5a724eB5e4',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const CADC_POLY_MAINNET = {
  symbol: 'CADC',
  network: 'POLY',
  env: 'mainnet',
  address: '0x74D9423a2886618522F7C992Edf647ea707A38eb',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const CADC_SOL_MAINNET = {
  symbol: 'CADC',
  network: 'SOL',
  env: 'mainnet',
  address: '52spLAAFjKMwhNJ3TL2PUAUGYGgXsbvipP4whRtBp82S',
  decimals: 6,
  operations: SEND_AND_RECEIVE,
  lifecycle: 'current',
} as const satisfies TokenContract

export const LEGACY_CADC_ETH_TESTNET = {
  symbol: 'CADC',
  network: 'ETH',
  env: 'testnet',
  address: '0xB3279ccEb64996b926465292495bD458D9f5DA03',
  decimals: 18,
  operations: RECEIVE_ONLY,
  lifecycle: 'legacy',
} as const satisfies TokenContract

export const TOKEN_CONTRACTS = [
  LEGACY_CADC_ARB_MAINNET,
  USDC_ARB_MAINNET,
  BRIDGED_USDC_ARB_MAINNET,
  USDC_AVAX_MAINNET,
  LEGACY_CADC_BASE_MAINNET,
  USDC_BASE_MAINNET,
  USDC_BSC_MAINNET,
  USDC_CELO_MAINNET,
  LEGACY_CADC_ETH_MAINNET,
  USDC_ETH_MAINNET,
  USDC_FTM_MAINNET,
  USDC_GNO_MAINNET,
  LEGACY_CADC_LIN_MAINNET,
  USDC_LIN_MAINNET,
  USDC_OP_MAINNET,
  BRIDGED_USDC_OP_MAINNET,
  LEGACY_CADC_POLY_MAINNET,
  USDC_POLY_MAINNET,
  BRIDGED_USDC_POLY_MAINNET,
  USDC_SOL_MAINNET,
  LEGACY_CADC_SOL_MAINNET,
  USDC_SX_MAINNET,
  CADC_ETH_MAINNET,
  CADC_BASE_MAINNET,
  CADC_POLY_MAINNET,
  CADC_SOL_MAINNET,
  USDC_ARB_TESTNET,
  USDC_AVAX_TESTNET,
  CADC_BASE_TESTNET,
  USDC_BASE_TESTNET,
  USDC_CELO_TESTNET,
  CADC_ETH_TESTNET,
  USDC_ETH_TESTNET,
  USDC_LIN_TESTNET,
  USDC_OP_TESTNET,
  USDC_POLY_TESTNET,
  USDC_SOL_TESTNET,
  LEGACY_CADC_SOL_TESTNET,
  CADC_SOL_TESTNET,
  LEGACY_CADC_ETH_TESTNET,
] as const satisfies readonly TokenContract[]

export const getTokenContracts = ({
  env,
  operation,
}: {
  env: TokenEnvironment
  operation: TokenOperation
}) =>
  TOKEN_CONTRACTS.filter(
    (contract) =>
      contract.env === env && contract.operations.includes(operation)
  )

export const getTokenContractsFor = ({
  env,
  network,
  symbol,
}: {
  env: TokenEnvironment
  network: ChainNetwork
  symbol: StablecoinSymbol
}) =>
  TOKEN_CONTRACTS.filter(
    (contract) =>
      contract.env === env &&
      contract.network === network &&
      contract.symbol === symbol
  )
